import React, { useCallback, useMemo } from 'react';

import { IItem } from '@rbi-ctg/menu';
import useEffectOnce from 'hooks/use-effect-once';
import { useFeatureDonation } from 'hooks/use-feature-donation';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import useDonation from 'state/order/hooks/use-donation';
import { useUIContext } from 'state/ui';

import { DonationAtCheckout } from '../donation-at-checkout';
import { IDonationAtCheckoutProps } from '../donation-at-checkout/types';

const DonationContainer = () => {
  const { featureDonation } = useFeatureDonation();
  const { pricingFunction } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();

  const donationItems = useMemo(
    () => featureDonation?.donationItemList?.map(item => ({ ...item } as IItem)) || [],
    [featureDonation?.donationItemList]
  );

  const {
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
    selectedDonationCartEntry,
  } = useDonation({
    donationItems,
  });

  const allDonationOptions: IDonationAtCheckoutProps['donationOptions'] = useMemo(
    () => [
      ...(donationItems.map(item => ({
        label: formatCurrencyForLocale(pricingFunction({ item })),
        value: item._id,
        isDisabled: false,
      })) as IDonationAtCheckoutProps['donationOptions']),
    ],
    [donationItems, formatCurrencyForLocale, pricingFunction]
  );

  const handleDonationToggleChange = useCallback(
    (newSelectedDonationId: string) => {
      if (selectedDonationCartEntry?._id === newSelectedDonationId) {
        handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry.cartId });
        return;
      }

      const optionItem = donationItems.find(item => item?._id === newSelectedDonationId);
      if (!optionItem) {
        return;
      }
      handleAddDonationCartEntry(optionItem);
    },
    [
      donationItems,
      handleAddDonationCartEntry,
      handleRemoveDonationCartEntry,
      selectedDonationCartEntry,
    ]
  );

  const haveAllItemsZeroPrice = (donationItems: IItem[]): boolean => {
    const total = donationItems.reduce(
      (accumulator, currentItem) => accumulator + pricingFunction({ item: currentItem }),
      0
    );

    return total <= 0;
  };

  useEffectOnce(() => {
    if (donationItems.length) {
      logRBIEvent({
        name: CustomEventNames.FOUNDATION_DONATIONS_VIEW,
        type: EventTypes.Other,
        attributes: {},
      });
    }
  });

  if (!donationItems.length || haveAllItemsZeroPrice(donationItems)) {
    return null;
  }

  return (
    <DonationAtCheckout
      header={featureDonation?.header?.locale ?? ''}
      subtitle={featureDonation?.subtitle?.locale ?? ''}
      donationOptions={allDonationOptions}
      selectedDonationId={selectedDonationCartEntry?._id}
      onChangeValue={handleDonationToggleChange}
    />
  );
};

export default DonationContainer;
